/**
 * useArTableFormulas
 *
 * React hook for reactive formula computation in ArTable.
 * Sets up formula derivations on rows.
 *
 * On load:
 *   - Attach derives to all existing rows
 *   - Subscribe to collection for row add/remove
 *
 * Maintain:
 *   - When rows added → attach derives
 *   - When rows removed → cleanup derives
 *   - When schema changes → re-attach with new formulas
 *
 * On unmount:
 *   - Cleanup all derives and subscriptions
 *
 * Skip semantics live in the shared engine's resolveArRowFormulaSkip — the
 * aggregations row, `suppressFormulas` rows (writer-set values are
 * authoritative), and bundle headers (bundles enabled on the table type AND
 * `bundleId` present, as `classifyArBundleRows` requires; a formula over a
 * header's mostly-empty cells coerces missing operands to 0 and persists junk).
 * A row with a stale `bundleRole` on a bundle-less table renders flat and keeps
 * its formulas. Kit headers still receive deriveKitRowCalc.
 *
 * The engine config is built from the fieldDataTypeId-enriched sequence, but
 * rebuild detection compares the RAW schema references so a config object
 * recreated every render never forces a re-attachment.
 *
 * `persist` reaches `deriveKitRowCalc` only. Formula derives take no persist —
 * the server row trigger is the sole writer of formula results (SYD-2292), so
 * kit roll-ups (`calcOutput`, a separate ticket) are the last client write here.
 */

import { useContext, useEffect, useRef } from 'react';
import { useStoreNonReactive, useStoreCollection, formStore } from '@/store';
import { FieldTypeContext } from '@/providers/FieldTypeContext';
import deriveRowFormulas from './core/deriveRowFormulas';
import { createArFormulaEngineConfig, parseFormula, resolveArRowFormulaSkip } from '#types';
import { deriveKitRowCalc, resolveKitRowColumnCalc } from '../arTableKitRowCalc';
import type {
  ArTableType,
  ArRowData,
  ArRowId,
  ArFormulaEntry,
  KitRowColumnCalc,
} from '#types';
import type { CollectionState, DocumentState } from '../Store.types';

const emptyArray: ArFormulaEntry[] = [];

/**
 * Hook for reactive formula computation in ArTable.
 * Call this from ArTableBody after stores are loaded.
 */
function useArTableFormulas(): void {
  const { state: schemaStateRaw } = useStoreNonReactive('schema');
  const { state: rowsStateRaw, persist } = useStoreCollection<ArRowData>('rows');
  const { fieldTypes } = useContext(FieldTypeContext);

  const schemaState = schemaStateRaw as DocumentState<ArTableType>;
  const rowsState = rowsStateRaw as CollectionState<ArRowData>;

  /**
   * Track per-row derive attachments. We intentionally keep this as a stable
   * mutable container (Valtio store pattern).
   *
   * NOTE: We access `ref.current` during render to ensure formula derives are
   * attached BEFORE row/cell components render and call `useSnapshot`.
   * If derives are attached in an effect, cells may render before the derived
   * properties exist, so they never subscribe to them and formulas won't update.
   */
  const attachmentsRef = useRef<Map<ArRowId, () => void> | null>(null);
  if (attachmentsRef.current === null) {
    attachmentsRef.current = new Map();
  }
  const attachments = attachmentsRef.current;

  const formulaSequenceRef = useRef<ArFormulaEntry[]>(emptyArray);
  const tableCalcsRef = useRef<KitRowColumnCalc[] | undefined>(undefined);
  const formCalcsRef = useRef<KitRowColumnCalc[] | undefined>(undefined);
  const bundlesEnabledRef = useRef<boolean | undefined>(undefined);

  const cleanupAll = (): void => {
    for (const cleanup of attachments.values()) {
      cleanup();
    }
    attachments.clear();
  };

  if (!schemaState.loading && !rowsState.loading) {
    const rawSequence = (schemaState.data?.formulaSequence ?? emptyArray) as ArFormulaEntry[];
    const tableCalcs = schemaState.data?.kitRowColumnCalc;
    const formCalcs = formStore.state.formType.data?.kitRowColumnCalc;
    const resolvedCalcs = resolveKitRowColumnCalc(tableCalcs, formCalcs);

    /** Entries are enriched with fieldDataTypeId from the field type registry
     * when missing; the engine config is built from the enriched sequence. */
    const formulaSequence: ArFormulaEntry[] = rawSequence.map((entry) => {
      if (entry.fieldDataTypeId) return entry;
      const fieldDataTypeId = fieldTypes[entry.typeId]?.fieldDataTypeId;
      return fieldDataTypeId ? { ...entry, fieldDataTypeId } : entry;
    });

    const config = createArFormulaEngineConfig({
      formulaSequence,
      bundles: schemaState.data?.bundles,
    });

    const sequenceChanged = rawSequence !== formulaSequenceRef.current;
    const calcsChanged =
      tableCalcs !== tableCalcsRef.current || formCalcs !== formCalcsRef.current;
    /** Toggling bundles moves header rows in and out of the skip below, so every
     * attachment must be rebuilt — otherwise a header keeps a derive it should have
     * lost, or never gains one it should have regained. */
    const bundlesChanged = config.bundlesEnabled !== bundlesEnabledRef.current;

    if (sequenceChanged || calcsChanged || bundlesChanged) {
      formulaSequenceRef.current = rawSequence;
      tableCalcsRef.current = tableCalcs;
      formCalcsRef.current = formCalcs;
      bundlesEnabledRef.current = config.bundlesEnabled;
      cleanupAll();
      for (const { formula } of formulaSequence) {
        try {
          parseFormula(formula);
        } catch (err) {
          console.error('[useArTableFormulas] Failed to parse formula:', formula, err);
        }
      }
    }

    const hasFormulaDerives = config.formulaSequence.length > 0;
    const hasKitCalcDerives = resolvedCalcs.length > 0;

    if (hasFormulaDerives || hasKitCalcDerives) {
      const currentRowIds = new Set(rowsState.docs.keys());

      for (const [rowId, rowEntry] of rowsState.docs) {
        const skipReason = resolveArRowFormulaSkip(rowId, rowEntry.data, config);
        if (skipReason === 'aggregationsRow' || skipReason === 'suppressFormulas') continue;
        if (attachments.has(rowId)) continue;

        const cleanups: Array<() => void> = [];

        if (hasFormulaDerives && skipReason === null) {
          cleanups.push(deriveRowFormulas(rowId, rowEntry, config));
        }

        const isKitHeader =
          rowEntry.data?.bundleRole === 'header' && rowEntry.data?.bundleType === 'kit';
        if (hasKitCalcDerives && isKitHeader) {
          cleanups.push(
            deriveKitRowCalc(rowId, rowEntry, resolvedCalcs, rowsState, persist)
          );
        }

        if (cleanups.length === 0) continue;
        attachments.set(rowId, () => {
          for (const c of cleanups) c();
        });
      }

      for (const rowId of Array.from(attachments.keys())) {
        if (!currentRowIds.has(rowId)) {
          const cleanup = attachments.get(rowId);
          if (cleanup) cleanup();
          attachments.delete(rowId);
        }
      }
    }
  }

  useEffect(() => {
    return () => {
      cleanupAll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export { useArTableFormulas };
